import {
  ButtonTertiary,
  cssColorValue,
  desktopBallad,
  desktopCelloCanon,
  HorizontalRule,
  IconArrowTopRight,
  IconChevronDown,
  IconChevronUp,
  mobileBallad,
  mobileCello,
  PopoverNavigation,
  PopoverNavigationItem,
  screenXsMin,
  semanticColors,
  spacer12,
  spacer20,
  spacer24,
  spacer32,
  spacer4,
  spacer40,
  spacer64,
  TextLink,
} from '@spotify-internal/encore-web';
import React, { useState } from 'react';
import styled from 'styled-components';

import { HeaderSchema, LanguageSelectionSchema, LinkList } from 'types/navigation';
import { useTranslation } from 'providers/translations/TranslationProvider';

import useDropdown, { DropdownContext } from './hooks/useDropdown';
import { isMastheadLinkList, PopoverLink } from './types';
import { NavBarLanguageItem } from './Language';

const StyledPopoverNavigation = styled(PopoverNavigation)`
  background: ${cssColorValue(semanticColors.backgroundBase)};
  bottom: 0;
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: ${spacer64} ${spacer20} ${spacer20};
  position: fixed;
  inset-inline-end: 0;
  top: 0;
  width: calc(${spacer24} * 10 + ${spacer40});

  &&:focus {
    outline: none;
  }
`;

const NavList = styled.ul`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: ${spacer20};
  list-style-type: none;
  margin-bottom: ${spacer32};
  margin-top: 0;
  padding: 0;
`;

const MenuSeparator = styled(HorizontalRule)`
  border-color: ${cssColorValue(semanticColors.essentialBase)};
  border-width: 1px;
  margin: ${spacer12} 0;
  width: ${spacer40};
`;

export const NavBarSideMenuDropdownBtn = styled(ButtonTertiary)`
  && {
    border-radius: 0;
    color: ${cssColorValue(semanticColors.textBase)};
    text-decoration: none;
    padding: 0;
    min-block-size: auto;
    ${desktopCelloCanon};

    &&:active,
    &&:hover,
    &&:focus {
      color: ${cssColorValue(semanticColors.textBase)};
      opacity: 1;
      text-decoration: none;
      transform: none;
    }

    &:focus {
      outline: 5px auto -webkit-focus-ring-color;
      outline-offset: ${spacer4};
    }

    &::after {
      display: none;
    }

    @media (max-width: ${screenXsMin}) {
      ${mobileCello}
    }
  }
`;

const NavBarSideMenuLink = styled(TextLink)`
  && {
    color: ${cssColorValue(semanticColors.textBase)};
    text-decoration: none;
    ${desktopCelloCanon};

    &&:active,
    &&:hover,
    &&:focus {
      box-shadow: none;
      color: ${cssColorValue(semanticColors.textBase)};
      text-decoration: none;
    }

    &:focus {
      outline: 5px auto -webkit-focus-ring-color;
      outline-offset: ${spacer4};
    }

    @media (max-width: ${screenXsMin}) {
      ${mobileCello}
    }
  }
`;

const StyledIconChevronDown = styled(IconChevronDown)`
  @media (max-width: ${screenXsMin}) {
    transform: translateY(2px);
  }
`;

const StyledIconChevronUp = styled(IconChevronUp)`
  @media (max-width: ${screenXsMin}) {
    transform: translateY(4px);
  }
`;

const SubMenuList = styled(NavList)`
  margin-bottom: 0;
  margin-inline-start: ${spacer20};
  margin-top: ${spacer20};
`;

const SubMenuLink = styled(ButtonTertiary)`
  color: ${cssColorValue(semanticColors.textSubdued)};
  cursor: pointer;
  justify-content: flex-start;
  padding: 0;
  min-block-size: auto;
  ${desktopBallad};

  &:active,
  &:hover,
  &:focus {
    color: ${cssColorValue(semanticColors.textSubdued)};
    transform: none;
  }

  &::after {
    bottom: 0;
    inline-size: 100%;
  }

  [dir='rtl'] & * {
    direction: rtl;
    & > svg {
      transform: rotateY(180deg);
    }
  }

  & > * {
    margin-inline-start: ${spacer4};
    transform: translateY(1px);
  }

  @media (max-width: ${screenXsMin}) {
    ${mobileBallad}
  }
`;

const StyledPopoverNavigationItem = styled(PopoverNavigationItem)`
  ${desktopCelloCanon};

  @media (max-width: ${screenXsMin}) {
    ${mobileCello}
  }
`;

type SideMenuAccordionProps = {
  linkListEntry: LinkList;
  headerEntry: HeaderSchema;
  closeBackdrop: () => void;
};

const SideMenuAccordion = ({ linkListEntry, headerEntry, closeBackdrop }: SideMenuAccordionProps) => {
  const { handlers, open } = useDropdown(linkListEntry.label, true);
  const { t } = useTranslation();

  const links = (linkListEntry.entries || []).map<PopoverLink>(linkListItem => {
    const link = linkListItem.link;
    return {
      href: link.href,
      linkText: t(link.title),
      level: linkListItem?.nestingLevel,
      target: link.target,
    };
  });

  return (
    <>
      <NavBarSideMenuDropdownBtn
        href=""
        iconTrailing={() =>
          open ? (
            <StyledIconChevronUp size="medium" aria-label={t(headerEntry.dropdownMenuLabelClose)} />
          ) : (
            <StyledIconChevronDown size="medium" aria-label={t(headerEntry.dropdownMenuLabelOpen)} />
          )
        }
        onKeyDown={handlers.handleOnKeyDown}
        onMouseDown={handlers.handleOnMouseDown}
      >
        {t(linkListEntry.label)}
      </NavBarSideMenuDropdownBtn>
      {open && (
        <SubMenuList>
          {links.map(({ linkText, target, href, level, ...rest }, i) => (
            <li key={i}>
              <SubMenuLink
                onClick={closeBackdrop}
                target={target}
                href={href}
                iconTrailing={target === '_blank' ? () => <IconArrowTopRight size="small" /> : undefined}
                {...rest}
              >
                {linkText}
              </SubMenuLink>
            </li>
          ))}
        </SubMenuList>
      )}
    </>
  );
};

type SideMenuSecondaryLinksProps = {
  headerEntry: HeaderSchema;
  closeBackdrop: () => void;
};

const SideMenuSecondaryLinks = ({ headerEntry, closeBackdrop }: SideMenuSecondaryLinksProps) => {
  const { t } = useTranslation();

  if (headerEntry.secondaryNav.length === 0) {
    return null;
  }

  return (
    <>
      {headerEntry.secondaryNav.map((item, i) => (
        <StyledPopoverNavigationItem onClick={closeBackdrop} key={i}>
          <NavBarSideMenuLink href={item.href} onClick={closeBackdrop} target={item.target}>
            {t(item.title)}
          </NavBarSideMenuLink>
        </StyledPopoverNavigationItem>
      ))}
      <StyledPopoverNavigationItem>
        <MenuSeparator />
      </StyledPopoverNavigationItem>
    </>
  );
};

type Props = {
  headerEntry: HeaderSchema;
  languageSelectionEntry: LanguageSelectionSchema;
  closeBackdrop: () => void;
  dir: string;
};

export const NavBarSideMenu = ({ headerEntry, languageSelectionEntry, closeBackdrop, dir }: Props) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const { t } = useTranslation();
  return (
    <StyledPopoverNavigation
      as="nav"
      dir={dir}
      role="dialog"
      tabIndex={0}
      aria-label={t(headerEntry.sideMenuPopoverAriaLabel)}
    >
      <NavList>
        <SideMenuSecondaryLinks headerEntry={headerEntry} closeBackdrop={closeBackdrop} />
        {headerEntry.centerNav.map((item, i) => {
          let content;
          if (isMastheadLinkList(item)) {
            content = (
              <DropdownContext.Provider value={{ activeDropdown, setActiveDropdown }}>
                <SideMenuAccordion headerEntry={headerEntry} linkListEntry={item} closeBackdrop={closeBackdrop} />
              </DropdownContext.Provider>
            );
          } else {
            content = (
              <NavBarSideMenuLink href={item.href} target={item.target} onClick={closeBackdrop}>
                {t(item.title)}
              </NavBarSideMenuLink>
            );
          }

          return <StyledPopoverNavigationItem key={i}>{content}</StyledPopoverNavigationItem>;
        })}
      </NavList>
      <div>
        <NavBarLanguageItem container="sidemenu" languageSelectionEntry={languageSelectionEntry} />
      </div>
    </StyledPopoverNavigation>
  );
};
